import { SDLCMetadata, SDLCMetadataTypeGuards, } from "@sscs/types";
import { capitalizeFirstLetter, compareValues, getDirnameFromComponentPath, } from "@sscs/utils";
import { formatDistanceToNow } from "date-fns";
import { REPORT_PAGES_ROUTES, REPORT_QUERY_PARAMS } from "../../constants";
export const POLICY_CATEGORIES = {
    containers: "containers",
    "diff-hunting": "diff-hunting",
    hardening: "hardening",
    hunting: "hunting",
    integrity: "integrity",
    licenses: "licenses",
    secrets: "secrets",
    signatures: "signatures",
    threats: "threats",
    vulnerabilities: "vulnerabilities",
    yara: "yara",
};
export const DEPENDENCY_LINKING_TO_SHORT_TEXT = {
    dynamic: "Dynamically linked",
    package: "Package",
    static: "Statically linked",
};
// Should match CLI
export const DEPENDENCY_LINKING_TO_LONG_TEXT = {
    dynamic: `${DEPENDENCY_LINKING_TO_SHORT_TEXT.dynamic} Dependency`,
    package: `${DEPENDENCY_LINKING_TO_SHORT_TEXT.package} Dependency`,
    static: `${DEPENDENCY_LINKING_TO_SHORT_TEXT.static} Dependency`,
};
export const PREVALENCE_TO_TEXT = {
    "behavior-common": "Common",
    "behavior-uncommon": "Uncommon",
    "behavior-anomalous": "Anomalous",
    "behavior-important": "Important",
    "behavior-malicious": "Malicious",
};
export const SECRET_LIVENESS_DESCRIPTION = {
    skipped: "Liveness check skipped",
    "site-unknown": "Server not defined, possible configuration error",
    "site-offline": "Server not reachable",
    "site-timeout": "Server timeout",
    "rate-limited": "Could not check due to rate limiting",
    "site-error": "",
    active: "Secret is active and valid",
    inactive: "Secret not accepted by server",
};
export function isPolicyCategory(category) {
    return Object.keys(POLICY_CATEGORIES).includes(category);
}
/**
 * Parses out and returns the license family portion of the identity_object_t license string.
 * Identity license string should always be in format "License family (spdx ID)".
 * @param license identity_object_t license string
 * @returns SDLCMetadata.license_family_t | undefined
 */
export const parseIdentityLicenseFamily = (license) => {
    var _a;
    const licenseFamily = (_a = license.split("(")) === null || _a === void 0 ? void 0 : _a[0].trim();
    if (licenseFamily &&
        SDLCMetadataTypeGuards.stringIsLicenseFamily(licenseFamily)) {
        return licenseFamily;
    }
    else {
        return undefined;
    }
};
/**
 * Parses out and returns the license family and the rest of the license string from identity_object_t.
 * Identity license string should always be in format "License family (spdx ID)".
 * @param license identity_object_t license string
 * @returns { family: SDLCMetadata.license_family_t, rest: string } | undefined
 */
export const parseIdentityLicense = (license) => {
    const licenseFamily = parseIdentityLicenseFamily(license);
    if (licenseFamily) {
        const rest = license.length > licenseFamily.length
            ? license.split(licenseFamily)[1]
            : "";
        return {
            family: licenseFamily,
            rest: rest,
        };
    }
    else {
        return undefined;
    }
};
/** Utility to get query status from object returned by RTK Query useQuery
 * @param isLoading returned from useQuery
 * @param isFetching returned from useQuery
 * @param isError returned from useQuery
 * @param error returned from useQuery
 * @param data returned from useQuery
 */
export function getQueryStatus({ isLoading, isFetching, isError, error, data, }) {
    if (isError)
        return { status: "ERROR", error: error };
    if (isLoading)
        return { status: "LOADING" };
    if (isFetching)
        return { status: "FETCHING" };
    if (!error && data !== undefined)
        return { status: "SUCCESS" };
    return { status: "UNKNOWN" };
}
// Scroll element into view - scrolls the nearest element scroller (the report
// content pane) if there is one that actually scrolls, otherwise falls back to
// the window (standalone report and SAFE Viewer window-scroll inside an
// unbounded body, so a matched pane can be present but never scroll).
export const scrollIntoView = (elementId, offset = 0) => {
    const element = document.getElementById(elementId);
    if (!element)
        return;
    const container = element.closest("[data-scroll-container]");
    if (container && container.scrollHeight > container.clientHeight) {
        container.scrollTo({
            top: element.getBoundingClientRect().top -
                container.getBoundingClientRect().top +
                container.scrollTop -
                offset,
            behavior: "smooth",
        });
    }
    else {
        window.scrollTo({
            top: element.getBoundingClientRect().top + window.scrollY - offset,
            behavior: "smooth",
        });
    }
};
function convertSeverityToNumber(severity) {
    switch (severity) {
        case "low":
            return 1;
        case "medium":
            return 2;
        case "high":
            return 3;
        default:
            return 0;
    }
}
function getSeverityOrGradeValue(value, hasSeverityProperty) {
    if (hasSeverityProperty) {
        return convertSeverityToNumber(value);
    }
    return value;
}
export function comparePrimarySortPropertyThenStatus(first, second, reverse) {
    const hasSeverityProperty = SDLCMetadataTypeGuards.stringIsIssueEstimate(first.primarySortProperty) &&
        SDLCMetadataTypeGuards.stringIsIssueEstimate(second.primarySortProperty);
    const comparisonValue = compareValues(getSeverityOrGradeValue(first.primarySortProperty, hasSeverityProperty), getSeverityOrGradeValue(second.primarySortProperty, hasSeverityProperty), reverse);
    if (comparisonValue === 0) {
        return compareValues(first.status, second.status, false);
    }
    return comparisonValue;
}
export const generateOverrideId = (override) => {
    return `${override.status}-${override.enabled}`;
};
export const generateFileBrowserLink = ({ componentId, componentPath, searchParams, fileBrowserPage, }) => {
    const itemLinkPath = getDirnameFromComponentPath(componentPath);
    const itemLinkSearchParams = new URLSearchParams(searchParams);
    itemLinkSearchParams.set(REPORT_QUERY_PARAMS.path, itemLinkPath ? `${itemLinkPath}/` : "");
    const itemLinkSearch = itemLinkSearchParams.toString();
    return {
        pathname: `${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${componentId}/${fileBrowserPage}`,
        search: itemLinkSearch,
    };
};
export const generateFileBrowserTabLink = (fileId, fileBrowserPage) => `${REPORT_PAGES_ROUTES.FILE_BROWSER_ROUTE}/${fileId}/${fileBrowserPage}`;
export function isFilteredByLink(filter, initFilter, key, filteredByLink) {
    return filter[key] !== initFilter[key] && filteredByLink;
}
export const exploitStatsToVulnerabilityMetrics = (exploitStats, totalCount) => {
    var _a, _b, _c, _d, _e;
    const result = new Array(SDLCMetadata.vulnerability_metrics_t.metrics_cnt_k).fill(0);
    result[SDLCMetadata.vulnerability_metrics_t.vuln_total_k] = totalCount;
    result[SDLCMetadata.vulnerability_metrics_t.vuln_exploit_k] = (_a = exploitStats.EXISTS) !== null && _a !== void 0 ? _a : 0;
    result[SDLCMetadata.vulnerability_metrics_t.vuln_malware_k] = (_b = exploitStats.MALWARE) !== null && _b !== void 0 ? _b : 0;
    result[SDLCMetadata.vulnerability_metrics_t.vuln_mandate_k] = (_c = exploitStats.MANDATE) !== null && _c !== void 0 ? _c : 0;
    result[SDLCMetadata.vulnerability_metrics_t.vuln_fixable_k] = (_d = exploitStats.FIXABLE) !== null && _d !== void 0 ? _d : 0;
    result[SDLCMetadata.vulnerability_metrics_t.vuln_triaged_k] = (_e = exploitStats.TRIAGED) !== null && _e !== void 0 ? _e : 0;
    return result;
};
export const getRemainingCertificateValidTime = (validTo) => {
    const validToDate = new Date(validTo);
    const today = new Date();
    return today.getTime() > validToDate.getTime()
        ? `Expired ${formatDistanceToNow(validToDate, { addSuffix: true })}`
        : formatDistanceToNow(validToDate);
};
export const normalizeActionName = (name) => {
    return name.split(/[_-]/).map(capitalizeFirstLetter).join(" ");
};
export const getPropertyLabel = (actionsMappings, do_action, property) => {
    var _a, _b, _c;
    var _d;
    return ((_d = (_c = (_b = (_a = actionsMappings[do_action]) === null || _a === void 0 ? void 0 : _a.properties[property]) === null || _b === void 0 ? void 0 : _b.localization) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : property);
};
//# sourceMappingURL=common.js.map